import React from 'react';
import { DAY_WIDTH, FIRST_COLUMN_GAP } from '../../GanttChartConstants';
import GanttChartDateHelper from '../../helpers/GanttChartDateHelper';
import sizeMe from 'react-sizeme';
import GanttChartRegistry from '../../helpers/registry/GanttChartRegistry';
import { GanttChartDataRow } from './GanttChartDataRow';
import { GanttChartDataTask } from './GanttChartDataTask';
import { GanttChartItem } from '../../models/GanttChartItem.model';

export interface DataViewPortProps {
    itemheight: number;
    data: GanttChartItem[];
    nowposition: number;
    upperBoundary: number;
    startDate: Date;
    displayCurrentDayIndicator: boolean;
    onTaskClick(item: GanttChartItem): void;
    chartConteinarWidth: number;
    chartScrollLeft: number;
    isTaskClickable?: (taskId: string) => boolean;
    bufferEmptyRowsCount?: number;
}
export const DataViewPort = (props: DataViewPortProps) => {
    const bufferEmptyRowsCount = props.bufferEmptyRowsCount ?? 5;

    const getContainerHeight = () => {
        const rows = GanttChartRegistry.getNumberOfRows(props.data);
        let new_height = rows > 0 ? rows * props.itemheight : 10;
        return new_height;
    };

    function normalizeDate(date: Date | string): Date {
        const normalizedDate = new Date(date);
        normalizedDate.setHours(12, 40, 0, 0);
        return normalizedDate;
    }

    const getStartOfDay = (date: Date): Date => {
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        return startOfDay;
    };

    const renderRows = () => {
        let result: React.JSX.Element[] = [];
        const groups = GanttChartRegistry.groupData(props.data);
        Object.keys(groups).forEach((key, i) => {
            const group = groups[key];
            if (group[0].type === 'header') {
                const otherItems = group.slice(1);
                result.push(
                    <GanttChartDataRow
                        key={key}
                        top={i * props.itemheight}
                        itemheight={props.itemheight}
                        isSticky={i === 0}>
                        <div
                            className="row-header"
                            style={{
                                width: DAY_WIDTH * FIRST_COLUMN_GAP,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: 'white',
                                zIndex: 9,
                            }}>
                            {group[0].name}
                        </div>

                        {otherItems.map((item: GanttChartItem) => {
                            let new_position =
                                GanttChartDateHelper.dateToPixel(
                                    item.start,
                                    props.nowposition,
                                    DAY_WIDTH
                                ) +
                                DAY_WIDTH * FIRST_COLUMN_GAP;
                            let new_width =
                                GanttChartDateHelper.dateToPixel(
                                    normalizeDate(item.end),
                                    props.nowposition,
                                    DAY_WIDTH
                                ) +
                                DAY_WIDTH -
                                new_position;
                            return (
                                <GanttChartDataTask
                                    id={item.id}
                                    onClick={props.onTaskClick}
                                    item={item}
                                    color={item.color}
                                    backgroundColor={item.backgroundColor}
                                    left={new_position}
                                    width={new_width}
                                    height={props.itemheight - 1}
                                    chartConteinarWidth={
                                        props.chartConteinarWidth
                                    }
                                    chartScrollLeft={props.chartScrollLeft}
                                />
                            );
                        })}
                    </GanttChartDataRow>
                );
            } else {
                const eventItem = group[0];
                result.push(
                    <GanttChartDataRow
                        rowNumber={eventItem.orderNo}
                        key={key}
                        top={i * props.itemheight}
                        itemheight={props.itemheight}>
                        <div
                            className="row-number"
                            style={{
                                width: DAY_WIDTH * FIRST_COLUMN_GAP,
                                height: props.itemheight,
                                position: 'sticky',
                                left: 0,
                                backgroundColor:
                                    eventItem.orderNo % 2 === 0
                                        ? 'white'
                                        : 'rgb(247, 247, 247)',
                                zIndex: 9,
                            }}>
                            <div
                                className={
                                    eventItem.isBlinkAnimationActive
                                        ? 'item-status blink-item'
                                        : 'item-status'
                                }
                                style={{ color: eventItem.itemStatusColor }}>
                                {eventItem.itemStatus}
                            </div>
                            <div> {eventItem.orderNo}</div>
                        </div>

                        {group.map((item: GanttChartItem) => {
                            let new_position =
                                GanttChartDateHelper.dateToPixel(
                                    item.start,
                                    props.nowposition,
                                    DAY_WIDTH
                                ) +
                                DAY_WIDTH * FIRST_COLUMN_GAP;
                            let new_width =
                                GanttChartDateHelper.dateToPixel(
                                    normalizeDate(item.end),
                                    props.nowposition,
                                    DAY_WIDTH
                                ) +
                                DAY_WIDTH -
                                new_position;

                            const taskClass =
                                (item.isClickable ? 'link-text' : '') +
                                (item.isBlinkAnimationActive
                                    ? ' blink-item'
                                    : '');

                            return (
                                <GanttChartDataTask
                                    className={taskClass}
                                    id={item.id}
                                    item={item}
                                    onClick={
                                        item.isClickable
                                            ? props.onTaskClick
                                            : () => {}
                                    }
                                    color={item.color}
                                    backgroundColor={item.backgroundColor}
                                    left={new_position}
                                    width={new_width}
                                    height={props.itemheight}
                                    chartConteinarWidth={
                                        props.chartConteinarWidth
                                    }
                                    chartScrollLeft={props.chartScrollLeft}
                                    isTaskClickable={props.isTaskClickable}
                                />
                            );
                        })}
                    </GanttChartDataRow>
                );
            }
        });

        for (let index = 0; index < bufferEmptyRowsCount; index++) {
            result.push(
                <GanttChartDataRow
                    rowNumber={result.length}
                    key={`empty-row-${result.length}`}
                    top={result.length * props.itemheight}
                    itemheight={props.itemheight}>
                    <div
                        className="row-number"
                        style={{
                            width: DAY_WIDTH * FIRST_COLUMN_GAP,
                            height: props.itemheight,
                            position: 'sticky',
                            left: 0,
                            backgroundColor:
                                result.length % 2 === 0
                                    ? 'white'
                                    : 'rgb(247, 247, 247)',
                            zIndex: 9,
                        }}>
                        <div className="item-status"></div>
                        <div>{result.length}</div>
                    </div>
                </GanttChartDataRow>
            );
        }

        return result;
    };

    return (
        <div
            id="timeLinedataViewPort"
            className="timeLine-main-data-viewPort"
            style={{ position: 'relative' }}>
            <div
                className="timeLine-main-data-container"
                style={{
                    height: getContainerHeight(),
                    width: props.upperBoundary,
                    maxWidth: props.upperBoundary,
                }}>
                {renderRows()}
            </div>
            {props.displayCurrentDayIndicator ? (
                <div
                    style={{
                        position: 'absolute',
                        left: `${
                            DAY_WIDTH * FIRST_COLUMN_GAP +
                            GanttChartDateHelper.dateToPixel(
                                new Date(),
                                props.nowposition,
                                DAY_WIDTH
                            ) -
                            GanttChartDateHelper.dateToPixel(
                                getStartOfDay(props.startDate),
                                props.nowposition,
                                DAY_WIDTH
                            )
                        }px`,
                        top: '30px',
                        bottom: 0,
                        width: '2.5px',
                        backgroundColor: '#b0d48c',
                        zIndex: 8,
                    }}></div>
            ) : (
                <></>
            )}
        </div>
    );
};

export default sizeMe({ monitorWidth: false, monitorHeight: true })(
    DataViewPort
);
